import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import {
  ShieldCheck,
  Download,
  Plus,
  Pencil,
  Trash2,
  X,
  Users,
  Calendar as CalendarIcon,
  ClipboardList,
  Check,
  Send,
  CircleAlert,
} from "lucide-react";

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Quality, downloadAuthed } from "@/lib/api";
import MonthlyView from "@/components/QualityMonthlyView";
import SurveyPrepView from "@/components/QualitySurveyPrep";
import QualityTimeline from "@/components/QualityTimeline";

/**
 * Quality Command — weekly, per-director quality-indicator workspace.
 *
 * User flow:
 *   1. Open the dialog. It fetches a report for the default range (This Week).
 *   2. Pick a range (This / Last / Rolling 7 / Custom).
 *   3. For each director tab, tick indicators per patient with initials.
 *   4. Add / rename / delete directors + indicators on the fly.
 *   5. Export → one XLSX with a tab per director for hand-off.
 *
 * Design notes:
 *   • Checks are debounced + upserted in bulk so ticking many cells in a row
 *     does one round trip per burst instead of one-per-click.
 *   • The whole report is re-fetched after director/indicator edits so we
 *     never render stale schema against fresh data.
 */
export default function QualityCommand({ open, onOpenChange }) {
  const [rangeType, setRangeType] = useState("this_week");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeDir, setActiveDir] = useState(null);
  const [editSchemaOpen, setEditSchemaOpen] = useState(false);
  // "week" is the original per-director tab view; "month" is the new KPI dashboard.
  const [viewMode, setViewMode] = useState("week");

  const params = useMemo(() => {
    if (rangeType === "custom") {
      return customStart && customEnd
        ? { range_type: "custom", start: customStart, end: customEnd }
        : null;
    }
    return { range_type: rangeType };
  }, [rangeType, customStart, customEnd]);

  const reload = useCallback(async () => {
    if (!params) return;
    setLoading(true);
    try {
      const r = await Quality.report(params);
      setReport(r);
      if (!activeDir && r.directors?.length) setActiveDir(r.directors[0].id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load quality report");
    } finally {
      setLoading(false);
    }
  }, [params, activeDir]);

  useEffect(() => {
    if (open) reload();
    // Reset active tab when we open so the first director is always active on open.
    if (open) setActiveDir(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, rangeType, customStart, customEnd]);

  const activeDirector = report?.directors?.find((d) => d.id === activeDir) || report?.directors?.[0];

  const download = () => {
    if (!params) return;
    downloadAuthed(Quality.exportXlsxUrl(params), `quality-command-${report?.start || "range"}.xlsx`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[1200px] w-[95vw] max-h-[92vh] overflow-hidden flex flex-col p-0 border border-[#e2e8f0]"
        data-testid="quality-command-dialog"
      >
        <DialogHeader className="px-6 pt-6 pb-0 border-b border-[#e2e8f0] bg-white">
          <div className="flex items-start justify-between gap-4 pb-4">
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-6 h-6 mt-0.5 text-[#0284c7]" />
              <div>
                <DialogTitle className="font-heading text-2xl tracking-tight text-[#0f172a]">
                  Quality Command
                </DialogTitle>
                <DialogDescription className="text-sm text-[#475569] mt-1 max-w-2xl">
                  7-day quality-indicator workspace. One tab per director. Track chart audits
                  and quality checks per patient, then export a single Excel workbook for the
                  weekly Directors&apos; huddle.
                </DialogDescription>
              </div>
            </div>
            <button
              onClick={() => onOpenChange(false)}
              className="p-1 rounded hover:bg-[#f1f5f9] text-[#475569] hover:text-[#0f172a] transition-colors duration-200"
              data-testid="quality-close"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Week / Month / Survey / Evidence view toggle */}
          <div className="flex items-center gap-0 mt-4 border-b border-[#e2e8f0]">
            {[
              { id: "week", label: "Weekly (by director)" },
              { id: "month", label: "Monthly (KPIs & goals)" },
              { id: "survey", label: "TJC Survey Prep" },
              { id: "evidence", label: "Evidence & Share" },
            ].map((opt) => (
              <button
                key={opt.id}
                onClick={() => setViewMode(opt.id)}
                data-testid={`quality-view-${opt.id}`}
                className={[
                  "px-4 py-2.5 text-xs font-semibold tracking-wide uppercase transition-colors duration-200 -mb-px border-b-2",
                  viewMode === opt.id
                    ? "text-[#0284c7] border-[#0284c7]"
                    : "text-[#475569] border-transparent hover:text-[#0f172a] hover:border-[#cbd5e1]",
                ].join(" ")}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Range controls — only shown in weekly view */}
          {viewMode === "week" && (
          <div className="flex flex-wrap items-end gap-3 mt-4 pb-4">
            <div className="flex gap-1 bg-[#f1f5f9] rounded-md p-1 border border-[#e2e8f0]">
              {[
                { id: "this_week", label: "This week" },
                { id: "last_week", label: "Last week" },
                { id: "rolling_7", label: "Rolling 7" },
                { id: "custom", label: "Custom" },
              ].map((opt) => (
                <button
                  key={opt.id}
                  onClick={() => setRangeType(opt.id)}
                  data-testid={`quality-range-${opt.id}`}
                  className={[
                    "px-3 py-1.5 text-xs font-semibold tracking-wide uppercase rounded transition-colors duration-200",
                    rangeType === opt.id
                      ? "bg-[#0284c7] text-white shadow-sm"
                      : "text-[#475569] hover:bg-white",
                  ].join(" ")}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {rangeType === "custom" && (
              <div className="flex items-end gap-2">
                <div>
                  <Label className="text-[10px] font-semibold tracking-widest uppercase text-[#475569]">Start</Label>
                  <Input
                    type="date"
                    value={customStart}
                    onChange={(e) => setCustomStart(e.target.value)}
                    className="mt-1 h-8 border-[#e2e8f0]"
                    data-testid="quality-custom-start"
                  />
                </div>
                <div>
                  <Label className="text-[10px] font-semibold tracking-widest uppercase text-[#475569]">End</Label>
                  <Input
                    type="date"
                    value={customEnd}
                    onChange={(e) => setCustomEnd(e.target.value)}
                    className="mt-1 h-8 border-[#e2e8f0]"
                    data-testid="quality-custom-end"
                  />
                </div>
              </div>
            )}
            {report?.start && (
              <div className="flex items-center gap-1.5 text-xs text-[#475569] italic flex-wrap">
                <CalendarIcon className="w-3.5 h-3.5" />
                {format(parseISO(report.start), "MMM d")} – {format(parseISO(report.end), "MMM d, yyyy")} ·{" "}
                {report.summary.total_patients} patients · {report.summary.total_admits} admits ·{" "}
                {report.summary.total_discharges} D/C
                {report.summary.open_tasks > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 rounded bg-[#fbebe7] text-[#b94a48] font-semibold not-italic">
                    {report.summary.open_tasks} open task{report.summary.open_tasks > 1 ? "s" : ""}
                  </span>
                )}
              </div>
            )}
            <div className="ml-auto flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setEditSchemaOpen((v) => !v)}
                data-testid="quality-toggle-manage"
                className="border-[#e2e8f0]"
              >
                <Users className="w-4 h-4 mr-1.5" />
                {editSchemaOpen ? "Done managing" : "Manage directors & indicators"}
              </Button>
              <Button
                onClick={download}
                disabled={!report}
                data-testid="quality-export-xlsx"
                className="bg-[#0284c7] hover:bg-[#0369a1] text-white"
              >
                <Download className="w-4 h-4 mr-1.5" />
                Download Excel
              </Button>
            </div>
          </div>
          )}
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col min-h-0">
          {viewMode === "month" ? (
            <MonthlyView />
          ) : viewMode === "survey" ? (
            <SurveyPrepView directors={report?.directors || []} />
          ) : viewMode === "evidence" ? (
            <div className="overflow-auto">
              <QualityTimeline />
            </div>
          ) : (
            <>
              {loading && !report && (
                <div className="p-8 text-center text-sm text-[#475569]">Loading quality report…</div>
              )}
              {report && (
            <>
              {editSchemaOpen && (
                <ManageDirectors
                  report={report}
                  onChange={reload}
                />
              )}
              {!editSchemaOpen && (
                <Tabs value={activeDirector?.id} onValueChange={setActiveDir} className="flex-1 flex flex-col min-h-0">
                  <TabsList className="mx-6 mt-4 w-fit bg-[#f1f5f9] border border-[#e2e8f0] flex-wrap h-auto">
                    {report.directors.map((d) => (
                      <TabsTrigger
                        key={d.id}
                        value={d.id}
                        data-testid={`quality-director-tab-${d.id}`}
                        className="data-[state=active]:bg-white data-[state=active]:text-[#0f172a] data-[state=active]:shadow"
                        style={{
                          borderLeft: `3px solid ${d.color || "#0284c7"}`,
                        }}
                      >
                        {d.name}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                  {report.directors.map((d) => (
                    <TabsContent
                      key={d.id}
                      value={d.id}
                      className="flex-1 overflow-auto px-6 py-4 mt-0"
                      data-testid={`quality-tab-content-${d.id}`}
                    >
                      <DirectorMatrix
                        director={d}
                        directors={report.directors}
                        indicators={report.indicators_by_director[d.id] || []}
                        patients={report.patients}
                        dates={report.dates}
                        checks={report.checks}
                        tasks={report.tasks || []}
                        onSaved={reload}
                      />
                    </TabsContent>
                  ))}
                </Tabs>
              )}
            </>
          )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// DirectorMatrix — one director tab's patient × indicator editable grid,
// with an "Assigned tasks" strip at the top and a per-row "+ Task" button
// so any director can flag an issue and hand it off to the right owner
// (e.g. a fall gets assigned from Quality Director to CNO for investigation).
// ---------------------------------------------------------------------------
function DirectorMatrix({ director, directors, indicators, patients, dates, checks, tasks, onSaved }) {
  // Local state cache: `${patient_key}__${indicator_id}` → { checked, initials, note }
  const initial = useMemo(() => {
    const m = {};
    // Only the latest check per (patient_key, indicator_id) is meaningful.
    for (const ck of checks) {
      const k = `${ck.patient_key}__${ck.indicator_id}`;
      const prev = m[k];
      if (!prev || (ck.updated_at || "") > (prev.updated_at || "")) m[k] = ck;
    }
    return m;
  }, [checks]);

  const [local, setLocal] = useState(initial);
  const [savingCount, setSavingCount] = useState(0);
  const [taskModal, setTaskModal] = useState(null); // { patient?, indicator? } — opens the assign dialog
  useEffect(() => setLocal(initial), [initial]);

  const cell = (patient_key, indicator_id) => local[`${patient_key}__${indicator_id}`] || {};

  // Tasks assigned TO this director (shown at the top of the tab).
  const myTasks = useMemo(
    () => (tasks || []).filter((t) => t.assigned_to_director_id === director.id),
    [tasks, director.id],
  );

  // Debounced bulk save on any change so we're not hitting the API on every keystroke.
  const [pending, setPending] = useState([]);
  useEffect(() => {
    if (!pending.length) return;
    setSavingCount((n) => n + 1);
    const t = setTimeout(async () => {
      try {
        await Quality.upsertChecks(pending);
        setPending([]);
        onSaved && onSaved();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to save checks");
      } finally {
        setSavingCount((n) => Math.max(0, n - 1));
      }
    }, 550);
    return () => clearTimeout(t);
  }, [pending, onSaved]);

  const upsert = (patient, indicator_id, patch) => {
    const k = `${patient.patient_key}__${indicator_id}`;
    const existing = local[k] || {};
    const next = {
      ...existing,
      patient_key: patient.patient_key,
      patient_name: patient.name,
      indicator_id,
      date: dates[dates.length - 1] || existing.date, // stamp to end-of-range for provenance
      ...patch,
    };
    setLocal({ ...local, [k]: next });
    setPending((prev) => {
      // Merge or replace the pending item for this cell
      const others = prev.filter(
        (i) => !(i.patient_key === next.patient_key && i.indicator_id === next.indicator_id),
      );
      return [
        ...others,
        {
          date: next.date,
          patient_key: next.patient_key,
          patient_name: next.patient_name,
          indicator_id: next.indicator_id,
          checked: !!next.checked,
          initials: next.initials || "",
          note: next.note || "",
        },
      ];
    });
  };

  return (
    <>
      {/* Tasks assigned to THIS director */}
      <TasksStrip tasks={myTasks} directors={directors} indicators={indicators} onChanged={onSaved} />

      {!patients?.length ? (
        <div className="rounded-lg border border-dashed border-[#e0d9c9] bg-[#faf7ef] px-5 py-8 text-center text-sm text-[#475569] mt-3">
          No patients in this range. Generate a day (paste your census) first, then reopen Quality Command.
        </div>
      ) : !indicators.length ? (
        <div className="rounded-lg border border-dashed border-[#e0d9c9] bg-[#faf7ef] px-5 py-8 text-center text-sm text-[#475569] mt-3">
          No indicators yet for <strong>{director.name}</strong>. Click <em>Manage directors &amp; indicators</em> above to add some.
        </div>
      ) : (
        <div className="min-w-max mt-3">
          <div className="mb-3 flex items-center justify-between text-xs text-[#475569]">
            <span className="italic">Tick each cell when the indicator is completed / audited. Add initials for accountability. Click <ClipboardList className="inline w-3 h-3" /> on any row to assign a task to another director.</span>
            {savingCount > 0 && <span className="text-[#0284c7] font-medium">Saving…</span>}
          </div>
          <table className="border-separate border-spacing-0 text-sm">
            <thead>
              <tr>
                <th
                  className="sticky left-0 z-10 bg-white text-left px-3 py-2 border-b border-r border-[#e2e8f0] font-semibold text-[#0f172a]"
                  style={{ minWidth: 260 }}
                >
                  Patient
                </th>
                <th className="px-2 py-2 border-b border-[#e2e8f0] text-xs font-medium text-[#475569]">Room</th>
                {indicators.map((ind) => (
                  <th
                    key={ind.id}
                    className="px-2 py-2 border-b border-[#e2e8f0] text-xs font-medium text-[#0f172a] whitespace-normal"
                    style={{ minWidth: 150, borderTop: `2px solid ${director.color || "#0284c7"}` }}
                  >
                    {ind.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {patients.map((p, idx) => (
                <tr key={p.patient_key} className={idx % 2 ? "bg-[#faf9f5]" : ""}>
                  <td
                    className="sticky left-0 bg-inherit px-3 py-2 border-b border-r border-[#e2e8f0] font-medium text-[#0f172a]"
                    data-testid={`quality-row-patient-${p.patient_key}`}
                  >
                    <div className="flex items-center gap-2">
                      <span>{p.name}</span>
                      <button
                        onClick={() => setTaskModal({ patient: p })}
                        className="p-1 text-[#475569] hover:text-[#0284c7] hover:bg-[#f0f9ff] rounded"
                        title="Assign a task about this patient"
                        data-testid={`quality-assign-task-${p.patient_key}`}
                      >
                        <ClipboardList className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                  <td className="px-2 py-2 border-b border-[#e2e8f0] text-xs font-mono text-[#475569]">
                    {p.room || ""}
                  </td>
                  {indicators.map((ind) => {
                    const c = cell(p.patient_key, ind.id);
                    return (
                      <td
                        key={ind.id}
                        className="px-2 py-1 border-b border-[#e2e8f0] align-middle"
                        data-testid={`quality-cell-${p.patient_key}-${ind.id}`}
                      >
                        <div className="flex items-center gap-1.5">
                          <input
                            type="checkbox"
                            checked={!!c.checked}
                            onChange={(e) => upsert(p, ind.id, { checked: e.target.checked })}
                            className="w-4 h-4 accent-[#0284c7] cursor-pointer"
                            data-testid={`quality-check-${p.patient_key}-${ind.id}`}
                          />
                          <input
                            type="text"
                            placeholder="init"
                            value={c.initials || ""}
                            maxLength={4}
                            onChange={(e) => upsert(p, ind.id, { initials: e.target.value.toUpperCase(), checked: true })}
                            className="w-14 border border-[#e2e8f0] rounded px-1.5 py-0.5 text-xs font-mono uppercase focus:outline-none focus:ring-1 focus:ring-[#0284c7]"
                            data-testid={`quality-initials-${p.patient_key}-${ind.id}`}
                          />
                          <button
                            onClick={() => setTaskModal({ patient: p, indicator: ind })}
                            className="p-0.5 text-[#c4c4c0] hover:text-[#d96c4e]"
                            title={`Assign a task about "${ind.label}" for ${p.name}`}
                            data-testid={`quality-cell-assign-${p.patient_key}-${ind.id}`}
                          >
                            <CircleAlert className="w-3 h-3" />
                          </button>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {taskModal && (
        <TaskAssignDialog
          open={!!taskModal}
          onOpenChange={(v) => !v && setTaskModal(null)}
          directors={directors}
          currentDirector={director}
          patient={taskModal.patient}
          indicator={taskModal.indicator}
          onCreated={() => {
            setTaskModal(null);
            onSaved && onSaved();
          }}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// TasksStrip — the accordion at the top of each director tab listing tasks
// assigned TO this director. Directors can complete / delete / reassign.
// ---------------------------------------------------------------------------
function TasksStrip({ tasks, directors, indicators, onChanged }) {
  const [expanded, setExpanded] = useState(true);
  const [completing, setCompleting] = useState(null); // task being completed → asks for initials
  const [reassigning, setReassigning] = useState(null); // task being reassigned

  if (!tasks?.length) return null;

  const openCount = tasks.filter((t) => t.status !== "completed").length;

  const complete = async (task, initials, note) => {
    try {
      await Quality.updateTask(task.id, {
        status: "completed",
        completed_by: initials || "",
        completion_note: note || "",
      });
      setCompleting(null);
      onChanged && (await onChanged());
      toast.success("Task marked complete");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to complete task");
    }
  };
  const reopen = async (task) => {
    await Quality.updateTask(task.id, { status: "open" });
    onChanged && (await onChanged());
  };
  const remove = async (task) => {
    if (!confirm("Delete this task?")) return;
    await Quality.deleteTask(task.id);
    onChanged && (await onChanged());
  };
  const reassign = async (task, new_director_id) => {
    await Quality.updateTask(task.id, { assigned_to_director_id: new_director_id });
    setReassigning(null);
    onChanged && (await onChanged());
    toast.success("Task reassigned");
  };
  const indicatorLabel = (id) => indicators.find((i) => i.id === id)?.label || "";

  return (
    <div className="rounded-lg border border-[#e2e8f0] bg-white overflow-hidden" data-testid="quality-tasks-strip">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-2 bg-[#faf7ef] hover:bg-[#f5f0dd] border-b border-[#e2e8f0] transition-colors"
      >
        <div className="flex items-center gap-2">
          <ClipboardList className="w-4 h-4 text-[#d96c4e]" />
          <span className="font-heading text-sm font-semibold text-[#0f172a]">
            Assigned tasks
          </span>
          {openCount > 0 && (
            <span className="px-1.5 py-0.5 rounded bg-[#fbebe7] text-[#b94a48] text-[10px] font-bold tracking-wide">
              {openCount} OPEN
            </span>
          )}
          <span className="text-xs text-[#475569]">· {tasks.length} total</span>
        </div>
        <span className="text-xs text-[#475569]">{expanded ? "Hide" : "Show"}</span>
      </button>
      {expanded && (
        <div className="divide-y divide-[#f0efe9]">
          {tasks.map((t) => (
            <div
              key={t.id}
              className={[
                "px-4 py-2.5 flex items-start justify-between gap-3 text-sm",
                t.status === "completed" ? "opacity-60" : "",
              ].join(" ")}
              data-testid={`quality-task-${t.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={[
                    "px-1.5 py-0.5 rounded text-[10px] font-bold tracking-widest uppercase",
                    t.status === "open" && "bg-[#fbebe7] text-[#b94a48]",
                    t.status === "in_progress" && "bg-[#fff6e5] text-[#8a6b1a]",
                    t.status === "completed" && "bg-[#f0f9ff] text-[#0284c7]",
                  ].filter(Boolean).join(" ")}>
                    {t.status.replace("_", " ")}
                  </span>
                  <span className="font-semibold text-[#0f172a]">{t.title}</span>
                  {t.patient_name && (
                    <span className="text-[11px] text-[#475569]">· {t.patient_name}</span>
                  )}
                  {t.indicator_id && indicatorLabel(t.indicator_id) && (
                    <span className="text-[11px] italic text-[#475569]">· {indicatorLabel(t.indicator_id)}</span>
                  )}
                  {t.due_date && (
                    <span className="text-[11px] text-[#475569]">· due {t.due_date}</span>
                  )}
                </div>
                {t.description && (
                  <p className="text-xs text-[#3f4643] mt-1">{t.description}</p>
                )}
                <div className="text-[11px] text-[#909590] mt-1">
                  {t.created_by ? `From ${t.created_by}` : "Assigned"} · {new Date(t.created_at).toLocaleDateString()}
                  {t.status === "completed" && t.completed_by && (
                    <> · Completed by {t.completed_by}{t.completion_note ? ` — ${t.completion_note}` : ""}</>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                {t.status !== "completed" ? (
                  <button
                    onClick={() => setCompleting(t)}
                    className="p-1.5 text-[#0284c7] hover:bg-[#f0f9ff] rounded"
                    title="Mark complete"
                    data-testid={`quality-task-complete-${t.id}`}
                  >
                    <Check className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    onClick={() => reopen(t)}
                    className="p-1.5 text-[#475569] hover:text-[#8a6b1a] hover:bg-[#fff6e5] rounded text-[11px]"
                    title="Reopen"
                    data-testid={`quality-task-reopen-${t.id}`}
                  >
                    Reopen
                  </button>
                )}
                <button
                  onClick={() => setReassigning(t)}
                  className="p-1.5 text-[#475569] hover:text-[#0284c7] hover:bg-[#f0f9ff] rounded"
                  title="Reassign to another director"
                  data-testid={`quality-task-reassign-${t.id}`}
                >
                  <Send className="w-4 h-4" />
                </button>
                <button
                  onClick={() => remove(t)}
                  className="p-1.5 text-[#475569] hover:text-[#b94a48] hover:bg-[#fbebe7] rounded"
                  title="Delete"
                  data-testid={`quality-task-delete-${t.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {completing && (
        <CompleteTaskDialog
          task={completing}
          onCancel={() => setCompleting(null)}
          onDone={complete}
        />
      )}
      {reassigning && (
        <ReassignTaskDialog
          task={reassigning}
          directors={directors}
          onCancel={() => setReassigning(null)}
          onDone={reassign}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// TaskAssignDialog — pick a director + write title/description/due
// ---------------------------------------------------------------------------
function TaskAssignDialog({ open, onOpenChange, directors, currentDirector, patient, indicator, onCreated }) {
  const [assignTo, setAssignTo] = useState(directors[0]?.id || "");
  const [title, setTitle] = useState(indicator ? `${indicator.label} — follow up` : "");
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [initials, setInitials] = useState("");
  const [busy, setBusy] = useState(false);

  const create = async () => {
    if (!title.trim() || !assignTo) return;
    setBusy(true);
    try {
      await Quality.createTask({
        title: title.trim(),
        description: description.trim(),
        assigned_to_director_id: assignTo,
        patient_key: patient?.patient_key || "",
        patient_name: patient?.name || "",
        indicator_id: indicator?.id || "",
        created_by: initials.trim(),
        due_date: dueDate,
      });
      onCreated && onCreated();
      toast.success("Task assigned");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to assign task");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="quality-task-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Assign task</DialogTitle>
          <DialogDescription className="text-[#475569]">
            Hand off a follow-up to another director.
            {patient && <> Attached to <strong>{patient.name}</strong>{indicator && <> · {indicator.label}</>}.</>}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[11px] font-semibold tracking-widest uppercase text-[#475569]">Assign to</Label>
            <select
              value={assignTo}
              onChange={(e) => setAssignTo(e.target.value)}
              className="mt-1 w-full border border-[#e2e8f0] rounded px-2 py-1.5 text-sm bg-white"
              data-testid="quality-task-assign-to"
            >
              {directors.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}{d.id === currentDirector.id ? " (me)" : ""}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-[11px] font-semibold tracking-widest uppercase text-[#475569]">Title</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Investigate fall — 305-A"
              className="mt-1 border-[#e2e8f0]"
              data-testid="quality-task-title"
              autoFocus
            />
          </div>
          <div>
            <Label className="text-[11px] font-semibold tracking-widest uppercase text-[#475569]">Description</Label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What happened, what needs to be done, any deadline detail"
              className="mt-1 w-full min-h-[70px] border border-[#e2e8f0] rounded px-2 py-1.5 text-sm resize-y focus:outline-none focus:ring-1 focus:ring-[#0284c7]"
              data-testid="quality-task-description"
            />
          </div>
          <div className="flex gap-3">
            <div className="flex-1">
              <Label className="text-[11px] font-semibold tracking-widest uppercase text-[#475569]">Due date</Label>
              <Input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="mt-1 border-[#e2e8f0]"
                data-testid="quality-task-due"
              />
            </div>
            <div className="w-28">
              <Label className="text-[11px] font-semibold tracking-widest uppercase text-[#475569]">From (initials)</Label>
              <Input
                value={initials}
                onChange={(e) => setInitials(e.target.value.toUpperCase())}
                placeholder="NK"
                maxLength={4}
                className="mt-1 border-[#e2e8f0] font-mono uppercase"
                data-testid="quality-task-initials"
              />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={create}
            disabled={busy || !title.trim() || !assignTo}
            className="bg-[#0284c7] hover:bg-[#0369a1] text-white"
            data-testid="quality-task-create"
          >
            <Send className="w-4 h-4 mr-1.5" /> Assign
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// CompleteTaskDialog — small prompt for initials + optional note before closing
// ---------------------------------------------------------------------------
function CompleteTaskDialog({ task, onCancel, onDone }) {
  const [initials, setInitials] = useState("");
  const [note, setNote] = useState("");
  return (
    <Dialog open onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="max-w-md" data-testid="quality-task-complete-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Complete task</DialogTitle>
          <DialogDescription className="text-[#475569]">
            {task.title}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[11px] font-semibold tracking-widest uppercase text-[#475569]">Your initials</Label>
            <Input
              value={initials}
              onChange={(e) => setInitials(e.target.value.toUpperCase())}
              placeholder="NK"
              maxLength={4}
              autoFocus
              className="mt-1 border-[#e2e8f0] font-mono uppercase"
              data-testid="quality-task-complete-initials"
            />
          </div>
          <div>
            <Label className="text-[11px] font-semibold tracking-widest uppercase text-[#475569]">Completion note (optional)</Label>
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="RCA filed, root cause = wet floor, restraint reviewed…"
              className="mt-1 border-[#e2e8f0]"
              data-testid="quality-task-complete-note"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="ghost" onClick={onCancel}>Cancel</Button>
          <Button
            onClick={() => onDone(task, initials.trim(), note.trim())}
            className="bg-[#0284c7] hover:bg-[#0369a1] text-white"
            data-testid="quality-task-complete-save"
          >
            <Check className="w-4 h-4 mr-1.5" /> Mark complete
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// ReassignTaskDialog — pick a new director to own the task
// ---------------------------------------------------------------------------
function ReassignTaskDialog({ task, directors, onCancel, onDone }) {
  const [target, setTarget] = useState(task.assigned_to_director_id);
  return (
    <Dialog open onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="max-w-md" data-testid="quality-task-reassign-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Reassign task</DialogTitle>
          <DialogDescription className="text-[#475569]">{task.title}</DialogDescription>
        </DialogHeader>
        <div>
          <Label className="text-[11px] font-semibold tracking-widest uppercase text-[#475569]">New owner</Label>
          <select
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            className="mt-1 w-full border border-[#e2e8f0] rounded px-2 py-1.5 text-sm bg-white"
            data-testid="quality-task-reassign-select"
          >
            {directors.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="ghost" onClick={onCancel}>Cancel</Button>
          <Button
            onClick={() => onDone(task, target)}
            className="bg-[#0284c7] hover:bg-[#0369a1] text-white"
            data-testid="quality-task-reassign-save"
          >
            Reassign
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// ManageDirectors — inline schema editor. Add, rename, delete directors and
// their indicators without leaving the dialog.
// ---------------------------------------------------------------------------
function ManageDirectors({ report, onChange }) {
  const [newDirName, setNewDirName] = useState("");
  const [newDirColor, setNewDirColor] = useState("#0284c7");
  const [busy, setBusy] = useState(false);
  const [editingDir, setEditingDir] = useState(null);
  const [editDirName, setEditDirName] = useState("");

  const addDirector = async () => {
    if (!newDirName.trim()) return;
    setBusy(true);
    try {
      await Quality.createDirector({ name: newDirName.trim(), color: newDirColor });
      setNewDirName("");
      onChange && (await onChange());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add director");
    } finally {
      setBusy(false);
    }
  };

  const renameDirector = async () => {
    if (!editingDir || !editDirName.trim()) return;
    setBusy(true);
    try {
      await Quality.updateDirector(editingDir.id, { name: editDirName.trim() });
      setEditingDir(null);
      onChange && (await onChange());
    } finally {
      setBusy(false);
    }
  };

  const removeDirector = async (id) => {
    if (!confirm("Remove this director and all their indicators from the workspace?\n(Historical checks are preserved.)")) return;
    setBusy(true);
    try {
      await Quality.deleteDirector(id);
      onChange && (await onChange());
    } finally {
      setBusy(false);
    }
  };

  const addIndicator = async (director_id, label) => {
    const clean = (label || "").trim();
    if (!clean) return;
    setBusy(true);
    try {
      await Quality.createIndicator({ director_id, label: clean });
      onChange && (await onChange());
    } finally {
      setBusy(false);
    }
  };
  const removeIndicator = async (id) => {
    if (!confirm("Remove this indicator? (Historical checks are preserved.)")) return;
    setBusy(true);
    try {
      await Quality.deleteIndicator(id);
      onChange && (await onChange());
    } finally {
      setBusy(false);
    }
  };

  const renameIndicator = async (id, label) => {
    setBusy(true);
    try {
      await Quality.updateIndicator(id, { label });
      onChange && (await onChange());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to rename indicator");
    } finally {
      setBusy(false);
    }
  };

  const setIndicatorTarget = async (id, target_pct) => {
    try {
      await Quality.updateIndicator(id, { target_pct });
      onChange && (await onChange());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to set target");
    }
  };

  return (
    <div className="flex-1 overflow-auto px-6 py-4 space-y-4" data-testid="quality-manage">
      <div className="rounded-lg border border-[#e2e8f0] bg-[#faf7ef] px-4 py-3">
        <p className="text-[11px] font-semibold tracking-widest uppercase text-[#475569] mb-2">
          Add a director
        </p>
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[200px]">
            <Input
              value={newDirName}
              onChange={(e) => setNewDirName(e.target.value)}
              placeholder="e.g. Risk Management, Chief of Staff"
              className="border-[#e2e8f0]"
              data-testid="quality-new-director-name"
            />
          </div>
          <div>
            <input
              type="color"
              value={newDirColor}
              onChange={(e) => setNewDirColor(e.target.value)}
              className="h-9 w-16 border border-[#e2e8f0] rounded cursor-pointer"
              data-testid="quality-new-director-color"
            />
          </div>
          <Button
            onClick={addDirector}
            disabled={busy || !newDirName.trim()}
            data-testid="quality-add-director"
            className="bg-[#0284c7] hover:bg-[#0369a1] text-white"
          >
            <Plus className="w-4 h-4 mr-1" /> Add director
          </Button>
        </div>
      </div>

      {report.directors.map((d) => (
        <div key={d.id} className="rounded-lg border border-[#e2e8f0] p-4" style={{ borderLeftWidth: 4, borderLeftColor: d.color || "#0284c7" }}>
          <div className="flex items-center justify-between">
            {editingDir?.id === d.id ? (
              <div className="flex items-center gap-2 flex-1">
                <Input
                  value={editDirName}
                  onChange={(e) => setEditDirName(e.target.value)}
                  className="max-w-md border-[#e2e8f0]"
                  data-testid={`quality-rename-director-${d.id}`}
                  autoFocus
                />
                <Button size="sm" onClick={renameDirector} disabled={busy} className="bg-[#0284c7] hover:bg-[#0369a1] text-white">
                  Save
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setEditingDir(null)}>
                  Cancel
                </Button>
              </div>
            ) : (
              <>
                <h3 className="font-heading text-lg text-[#0f172a]">{d.name}</h3>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => {
                      setEditingDir(d);
                      setEditDirName(d.name);
                    }}
                    className="p-1.5 text-[#475569] hover:text-[#0284c7] hover:bg-[#f0f9ff] rounded"
                    data-testid={`quality-edit-director-${d.id}`}
                    title="Rename"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => removeDirector(d.id)}
                    className="p-1.5 text-[#475569] hover:text-[#b94a48] hover:bg-[#fbebe7] rounded"
                    data-testid={`quality-delete-director-${d.id}`}
                    title="Remove"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </>
            )}
          </div>
          <IndicatorList
            indicators={report.indicators_by_director[d.id] || []}
            onAdd={(label) => addIndicator(d.id, label)}
            onRemove={removeIndicator}
            onRename={renameIndicator}
            onTarget={setIndicatorTarget}
            director_id={d.id}
          />
        </div>
      ))}
    </div>
  );
}

function IndicatorList({ indicators, onAdd, onRemove, onRename, onTarget, director_id }) {
  const [newLabel, setNewLabel] = useState("");
  const [editing, setEditing] = useState(null); // { id, label }

  return (
    <div className="mt-3 space-y-1.5">
      {indicators.map((ind) => (
        <div key={ind.id} className="flex items-center justify-between text-sm bg-[#faf9f5] rounded px-3 py-1.5 border border-[#efece2]">
          {editing?.id === ind.id ? (
            <div className="flex items-center gap-2 flex-1">
              <Input
                value={editing.label}
                onChange={(e) => setEditing({ ...editing, label: e.target.value })}
                className="border-[#e2e8f0]"
                data-testid={`quality-rename-indicator-${ind.id}`}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter" && editing.label.trim()) {
                    onRename(ind.id, editing.label.trim());
                    setEditing(null);
                  } else if (e.key === "Escape") {
                    setEditing(null);
                  }
                }}
              />
              <Button
                size="sm"
                onClick={() => {
                  if (editing.label.trim()) {
                    onRename(ind.id, editing.label.trim());
                    setEditing(null);
                  }
                }}
                className="bg-[#0284c7] hover:bg-[#0369a1] text-white"
                data-testid={`quality-save-indicator-${ind.id}`}
              >
                Save
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
                Cancel
              </Button>
            </div>
          ) : (
            <>
              <span className="text-[#0f172a] flex-1" data-testid={`quality-indicator-${ind.id}`}>
                {ind.label}
              </span>
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-1 text-[10px] text-[#475569]">
                  Target
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={ind.target_pct || ""}
                    placeholder="—"
                    onChange={(e) => {
                      const val = e.target.value === "" ? 0 : parseInt(e.target.value, 10);
                      if (Number.isFinite(val) && val >= 0 && val <= 100) {
                        onTarget(ind.id, val);
                      }
                    }}
                    className="w-14 border border-[#e2e8f0] rounded px-1.5 py-0.5 text-xs font-mono text-center focus:outline-none focus:ring-1 focus:ring-[#0284c7]"
                    data-testid={`quality-indicator-target-${ind.id}`}
                    title="Compliance goal percentage (0-100). Leave blank for no target."
                  />
                  %
                </label>
                <button
                  onClick={() => setEditing({ id: ind.id, label: ind.label })}
                  className="p-1 text-[#475569] hover:text-[#0284c7] hover:bg-[#f0f9ff] rounded"
                  data-testid={`quality-edit-indicator-${ind.id}`}
                  title="Rename"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => onRemove(ind.id)}
                  className="p-1 text-[#475569] hover:text-[#b94a48] hover:bg-[#fbebe7] rounded"
                  data-testid={`quality-delete-indicator-${ind.id}`}
                  title="Remove"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </>
          )}
        </div>
      ))}
      <div className="flex items-center gap-2 pt-2">
        <Input
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          placeholder="Add indicator (e.g. MTP Updated, Q15 Audit)"
          className="border-[#e2e8f0]"
          data-testid={`quality-new-indicator-${director_id}`}
          onKeyDown={(e) => {
            if (e.key === "Enter" && newLabel.trim()) {
              onAdd(newLabel);
              setNewLabel("");
            }
          }}
        />
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            if (newLabel.trim()) {
              onAdd(newLabel);
              setNewLabel("");
            }
          }}
          data-testid={`quality-add-indicator-${director_id}`}
          className="border-[#e2e8f0]"
        >
          <Plus className="w-4 h-4 mr-1" /> Add
        </Button>
      </div>
    </div>
  );
}
